import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Question, Questionnaire } from '@app/core/models/questionnaire.model';

@Component({
  selector: 'app-ranking-order',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ranking-order.html',
  styleUrl: './ranking-order.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RankingOrder),
      multi: true
    }
  ]
})
export class RankingOrder implements OnInit {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() questionNumber: number = 0;
  @Input() isInvalid: boolean = false;
  @Input() errorMessage: string = '';
  @Input() formControl?: FormControl;

  @Output() answerChange = new EventEmitter<{ questionId: string, options: { optionId: string }[] }>();

  rankedItems: any[] = [];
  draggedItem: any = null;
  draggedIndex: number = -1;
  disabled = false;

  onChange = (value: string[]) => {};
  onTouched = () => {};

  ngOnInit() {
    if (this.question?.questionOptions) {
      this.rankedItems = [...this.question.questionOptions].map((item, index) => ({
        ...item,
        currentRank: index + 1
      }));
    }

    // Emit default payload with initial order
    if (this.question?.questionId && this.rankedItems.length > 0) {
      // Use setTimeout to ensure component is fully initialized
      setTimeout(() => {
        this.emitInitialValue();
      }, 0);
    }

    if (this.formControl) {
      this.formControl.valueChanges.subscribe(value => {
        if (value && Array.isArray(value)) {
          this.reorderItemsByValue(value);
        }
      });
    }
  }

  writeValue(value: string[]): void {
    if (value && Array.isArray(value)) {
      this.reorderItemsByValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  reorderItemsByValue(value: string[]) {
    if (this.question?.questionOptions) {
      const orderedIds = [...value];
      this.rankedItems = orderedIds
        .map(id => this.question!.questionOptions!.find(opt => opt.optionId === id))
        .filter(item => item)
        .map((item, index) => ({
          ...item,
          currentRank: index + 1
        }));
    }
  }

  onDragStart(event: DragEvent, item: any, index: number) {
    if (this.disabled) return;
    this.draggedItem = item;
    this.draggedIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', '');
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    
    if (this.draggedIndex !== dropIndex && this.draggedItem) {
      const draggedItem = this.rankedItems.splice(this.draggedIndex, 1)[0];
      this.rankedItems.splice(dropIndex, 0, draggedItem);
      this.updateRanks();
      this.emitValue();
    }
    
    this.draggedItem = null;
    this.draggedIndex = -1;
  }

  onDragEnd(event: DragEvent) {
    this.draggedItem = null;
    this.draggedIndex = -1;
  }

  moveItemUp(index: number) {
    if (this.disabled || index === 0) return;
    const item = this.rankedItems.splice(index, 1)[0];
    this.rankedItems.splice(index - 1, 0, item);
    this.updateRanks();
    this.emitValue();
  }

  moveItemDown(index: number) {
    if (this.disabled || index >= this.rankedItems.length - 1) return;
    const item = this.rankedItems.splice(index, 1)[0];
    this.rankedItems.splice(index + 1, 0, item);
    this.updateRanks();
    this.emitValue();
  }

  private updateRanks() {
    this.rankedItems.forEach((item, index) => {
      item.currentRank = index + 1;
    });
  }

  emitInitialValue() {
    // Emit default payload with initial order
    const optionIds = this.rankedItems.map(item => item.optionId);
    const options = optionIds.map(id => ({ optionId: id }));
    
    this.onChange(optionIds);
    this.onTouched();

    if (this.question?.questionId) {
      this.answerChange.emit({
        questionId: this.question.questionId,
        options: options
      });
    }

    if (this.formControl) {
      this.formControl.setValue(optionIds, { emitEvent: false });
    }
  }

  emitValue() {
    // Emit updated payload when order changes
    const optionIds = this.rankedItems.map(item => item.optionId);
    const options = optionIds.map(id => ({ optionId: id }));
    
    this.onChange(optionIds);
    this.onTouched();

    if (this.question?.questionId) {
      this.answerChange.emit({
        questionId: this.question.questionId,
        options: options
      });
    }

    if (this.formControl) {
      this.formControl.setValue(optionIds, { emitEvent: false });
      this.formControl.markAsTouched();
    }
  }

  // getRankIcon(rank: number): string {
  //   switch (rank) {
  //     case 1: return '🥇';
  //     case 2: return '🥈';
  //     case 3: return '🥉';
  //     default: return `${rank}`;
  //   }
  // }

  // getRankClass(rank: number): string {
  //   if (rank <= 3) return 'top-rank';
  //   return 'regular-rank';
  // }
}
